import { useEffect, useId, useRef, useState, type MutableRefObject } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { FailureModeTreeSelect, type FailureModeTreeOption } from "@/components/qc/FailureModeTreeSelect";
import { OperatorScriptSteps } from "@/components/qc/OperatorScriptSteps";
import { HubUserAvatar } from "@/components/HubUserAvatar";
import { Button } from "@/components/ui/Button";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { useIntegratorMe } from "@/lib/me-query";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";

import {
  applyQcOperatorOutcome,
  fetchQcFailureModes,
  fetchQcIncidentSerialHints,
  openIncident,
  PCBA_PRODUCT_FAMILY_OPTIONS,
  type QcIncident,
  type QcOperatorScriptRun,
  type QcIncidentPcbaProductFamily,
  type QcIncidentPcbaTlaVariant,
  type QcIncidentSubtype,
  type QcIncidentType,
} from "@/pages/manufacturing/qcIncidentsApi";

export interface OpenIncidentFormProps {
  onSuccess: (incident: QcIncident, notice?: string) => void;
  onCancel?: () => void;
  /** Header close while a script is showing applies the Tier 1 ending. */
  dismissRef?: MutableRefObject<(() => void) | null>;
  defaultType?: QcIncidentType;
  /** When set, type is locked (for example pre-configuration PCBA incidents). */
  fixedType?: QcIncidentType;
  defaultSubtype?: QcIncidentSubtype | null;
  serialPrefill?: string;
  defaultPcbaProductFamily?: QcIncidentPcbaProductFamily | "";
  defaultPcbaTlaVariant?: QcIncidentPcbaTlaVariant | "";
  defaultIssue?: string;
  defaultIssueNotes?: string;
  identityPrefill?: {
    deviceLabelSerial?: string;
    clampSerial?: string;
    mcuUuid?: string;
  };
}

function todayLabel(): string {
  return new Date().toLocaleDateString("en-US", {
    timeZone: "America/Chicago",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function defaultSubtypeForType(type: QcIncidentType): QcIncidentSubtype | null {
  return type === "clamp" ? "large" : null;
}

type FormIncidentType = QcIncidentType | "";

function formText(formData: FormData, name: string): string {
  const value = formData.get(name);
  return typeof value === "string" ? value.trim() : "";
}

function focusNextFormControl(form: HTMLFormElement, current: HTMLElement): void {
  const controls = Array.from(
    form.querySelectorAll<HTMLElement>(
      "input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])",
    ),
  ).filter((element) => element.tabIndex >= 0 && element.offsetParent !== null);
  const index = controls.indexOf(current);
  const next = index >= 0 ? controls[index + 1] : null;
  next?.focus();
}

export function OpenIncidentForm({
  onSuccess,
  onCancel,
  dismissRef,
  defaultType,
  fixedType,
  defaultSubtype,
  serialPrefill = "",
  defaultPcbaProductFamily = "",
  defaultPcbaTlaVariant = "",
  defaultIssueNotes = "",
  identityPrefill,
}: OpenIncidentFormProps) {
  const lockedType = fixedType ?? null;
  const initialType: FormIncidentType = lockedType ?? defaultType ?? "";
  const typeFieldId = useId();
  const subtypeFieldId = useId();
  const pcbaProductFamilyFieldId = useId();
  const pcbaTlaVariantFieldId = useId();
  const serialFieldId = useId();
  const failureModeFieldId = useId();
  const notesFieldId = useId();
  const serialInputRef = useRef<HTMLInputElement>(null);
  const hintGenerationRef = useRef(0);
  const fieldsTouchedRef = useRef({
    type: false,
    subtype: false,
    pcbaProductFamily: false,
    pcbaTlaVariant: false,
  });
  const meQuery = useIntegratorMe();

  const [type, setType] = useState<FormIncidentType>(initialType);
  const [subtype, setSubtype] = useState<QcIncidentSubtype | null>(
    defaultSubtype ?? (initialType !== "" ? defaultSubtypeForType(initialType) : null),
  );
  const [pcbaProductFamily, setPcbaProductFamily] = useState<QcIncidentPcbaProductFamily | "">(
    defaultPcbaProductFamily,
  );
  const [pcbaTlaVariant, setPcbaTlaVariant] = useState<QcIncidentPcbaTlaVariant | "">(defaultPcbaTlaVariant);
  const [serial, setSerial] = useState(serialPrefill);
  const [failureModes, setFailureModes] = useState<FailureModeTreeOption[]>([]);
  const [issueNotes, setIssueNotes] = useState(defaultIssueNotes);
  const [error, setError] = useState<string | null>(null);
  const [openIncidentId, setOpenIncidentId] = useState<number | null>(null);
  const [serialHintsLoading, setSerialHintsLoading] = useState(false);

  useFloorPageScanCapture({
    inputRef: serialInputRef,
    onScan: (value) => setSerial(value),
  });

  const failureModesQuery = useQuery({
    queryKey: ["qc-failure-modes"],
    queryFn: fetchQcFailureModes,
  });

  const [scriptRun, setScriptRun] = useState<{
    incident: QcIncident;
    scripts: QcOperatorScriptRun[];
    index: number;
    selected: Array<"tier1" | "repair" | "samples_only">;
    responses: Array<{
      scriptName: string;
      answerLabel: string;
      outcome: "tier1" | "repair" | "scrap" | "samples_only";
    }>;
  } | null>(null);
  const [outcomeError, setOutcomeError] = useState<string | null>(null);
  const [outcomePending, setOutcomePending] = useState(false);
  const [followPage, setFollowPage] = useState<{
    outcome: "tier1" | "repair" | "scrap" | "samples_only";
    lines: string[];
  } | null>(null);

  const createMutation = useMutation({
    mutationFn: openIncident,
    onSuccess: (incident) => {
      setError(null);
      const scripts = incident.operatorScripts ?? [];
      if (scripts.length > 0) {
        setScriptRun({ incident, scripts, index: 0, selected: [], responses: [] });
        return;
      }
      onSuccess(incident);
    },
    onError: (mutationError: Error) => {
      setError(mutationError.message);
    },
  });

  useEffect(() => {
    serialInputRef.current?.focus();
  }, []);

  useEffect(() => {
    const trimmed = serial.trim();
    hintGenerationRef.current += 1;
    const generation = hintGenerationRef.current;
    fieldsTouchedRef.current = {
      type: false,
      subtype: false,
      pcbaProductFamily: false,
      pcbaTlaVariant: false,
    };
    setOpenIncidentId(null);

    if (trimmed.length < 2) {
      setSerialHintsLoading(false);
      if (lockedType == null && defaultType == null) {
        setType("");
        setSubtype(null);
        setPcbaProductFamily("");
        setPcbaTlaVariant("");
      }
      return;
    }

    setSerialHintsLoading(true);
    const timer = window.setTimeout(() => {
      void fetchQcIncidentSerialHints(trimmed)
        .then((hints) => {
          if (generation !== hintGenerationRef.current) {
            return;
          }
          setOpenIncidentId(hints.openIncidentId);
          if (
            lockedType == null &&
            defaultType == null &&
            hints.suggestedType != null &&
            !fieldsTouchedRef.current.type
          ) {
            setType(hints.suggestedType);
            if (hints.suggestedType === "clamp" && hints.suggestedSubtype != null) {
              if (!fieldsTouchedRef.current.subtype) {
                setSubtype(hints.suggestedSubtype);
              }
            } else if (hints.suggestedType !== "clamp") {
              setSubtype(defaultSubtypeForType(hints.suggestedType));
            }
          }
          if (hints.suggestedType === "pcba" || lockedType === "pcba") {
            if (
              hints.pcbaProductFamily != null &&
              !fieldsTouchedRef.current.pcbaProductFamily
            ) {
              setPcbaProductFamily(hints.pcbaProductFamily);
            }
            if (hints.pcbaTlaVariant != null && !fieldsTouchedRef.current.pcbaTlaVariant) {
              setPcbaTlaVariant(hints.pcbaTlaVariant);
            }
          }
        })
        .catch(() => {
          if (generation === hintGenerationRef.current) {
            setOpenIncidentId(null);
          }
        })
        .finally(() => {
          if (generation === hintGenerationRef.current) {
            setSerialHintsLoading(false);
          }
        });
    }, 50);

    return () => {
      window.clearTimeout(timer);
    };
  }, [defaultType, lockedType, serial]);

  const effectiveType = lockedType ?? (type === "" ? null : type);

  const openerDisplay =
    meQuery.data?.user.displayName?.trim() || meQuery.data?.user.email || "Signed-in user";

  const activeScript = scriptRun == null ? null : scriptRun.scripts[scriptRun.index] ?? null;

  const resultCopy: Record<"tier1" | "repair" | "scrap" | "samples_only", string> = {
    tier1: "Deliver the device to Tier 1 review.",
    samples_only: "Set the device aside for samples only.",
    repair: "The device is repaired and can continue production.",
    scrap: "Scrap the device and set it aside.",
  };

  function winningOutcome(
    selected: Array<"tier1" | "repair" | "samples_only">,
  ): "tier1" | "repair" | "samples_only" {
    if (selected.includes("tier1")) return "tier1";
    if (selected.includes("samples_only")) return "samples_only";
    return "repair";
  }

  function chooseAnswer(answerLabel: string, outcome: "tier1" | "repair" | "scrap" | "samples_only"): void {
    if (scriptRun == null || activeScript == null || outcomePending) return;
    const responses = [
      ...scriptRun.responses,
      { scriptName: activeScript.name, answerLabel, outcome },
    ];
    if (outcome === "scrap") {
      setScriptRun({ ...scriptRun, responses });
      setFollowPage({ outcome: "scrap", lines: [resultCopy.scrap] });
      return;
    }
    const selected = [...scriptRun.selected, outcome];
    const nextIndex = scriptRun.index + 1;
    if (nextIndex < scriptRun.scripts.length) {
      setScriptRun({ ...scriptRun, index: nextIndex, selected, responses });
      setFollowPage(null);
      return;
    }
    const winner = winningOutcome(selected);
    setScriptRun({ ...scriptRun, selected, responses });
    setFollowPage({ outcome: winner, lines: [resultCopy[winner]] });
  }

  async function chooseOutcome(
    outcome: "tier1" | "repair" | "scrap" | "samples_only",
    note: string,
  ): Promise<void> {
    if (scriptRun == null || outcomePending) return;
    setOutcomePending(true);
    setOutcomeError(null);
    try {
      const result = await applyQcOperatorOutcome(scriptRun.incident.id, outcome, note, scriptRun.responses);
      onSuccess(result.incident, result.message);
    } catch (error) {
      setOutcomeError(error instanceof Error ? error.message : String(error));
      setOutcomePending(false);
    }
  }

  useEffect(() => {
    if (dismissRef == null) return;
    dismissRef.current =
      followPage != null
        ? () => {
            void chooseOutcome(followPage.outcome, followPage.lines.join(" "));
          }
        : null;
    return () => {
      dismissRef.current = null;
    };
  });

  if (scriptRun != null && activeScript != null) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {followPage == null ? activeScript.name : "Result"}
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            NCR {scriptRun.incident.id}
            {followPage == null && scriptRun.scripts.length > 1
              ? `, script ${scriptRun.index + 1} of ${scriptRun.scripts.length}`
              : ""}
          </p>
        </div>
        {followPage == null ? (
          <>
            <OperatorScriptSteps text={activeScript.steps} />
            {activeScript.question != null && activeScript.question.trim() !== "" ? (
              <p className="text-sm font-medium text-slate-900">{activeScript.question}</p>
            ) : null}
          </>
        ) : (
          <div className="space-y-3 text-sm text-slate-800">
            {followPage.lines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        )}
        {outcomeError != null ? (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {outcomeError}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
          {followPage != null ? (
            <Button
              type="button"
              disabled={outcomePending}
              onClick={() => void chooseOutcome(followPage.outcome, followPage.lines.join(" "))}
            >
              Done
            </Button>
          ) : (
            <>
              {(activeScript.options.some((option) => option.outcome === "tier1")
                ? activeScript.options
                : [
                    ...activeScript.options,
                    {
                      label: activeScript.options.length === 0 ? "Done" : "No",
                      outcome: "tier1" as const,
                      followUp: "Deliver the device to Tier 1 review.",
                    },
                  ]
              ).map((option) => (
                <Button
                  key={`${option.outcome}-${option.label}`}
                  type="button"
                  variant={option.outcome === "tier1" ? "primary" : "secondary"}
                  disabled={outcomePending}
                  onClick={() => chooseAnswer(option.label, option.outcome)}
                >
                  {option.label.trim() === "" ? "Answer" : option.label}
                </Button>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      onKeyDown={(event) => {
        if (event.key !== "Enter") {
          return;
        }
        if (event.ctrlKey) {
          event.preventDefault();
          event.currentTarget.requestSubmit();
          return;
        }
        if (event.target instanceof HTMLTextAreaElement) {
          return;
        }
        if (event.target instanceof HTMLButtonElement) {
          return;
        }
        if (event.target instanceof HTMLElement) {
          event.preventDefault();
          focusNextFormControl(event.currentTarget, event.target);
        }
      }}
      onSubmit={(event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const submittedTypeRaw = lockedType ?? formText(formData, "type");
        const submittedSubtype = formText(formData, "subtype") as QcIncidentSubtype | "";
        const trimmedSerial = formText(formData, "serial");
        const submittedIssueNotes = formText(formData, "issue_notes");
        const submittedPcbaProductFamily = formText(formData, "pcba_product_family") as
          | QcIncidentPcbaProductFamily
          | "";
        const submittedPcbaTlaVariant = formText(formData, "pcba_tla_variant") as
          | QcIncidentPcbaTlaVariant
          | "";
        if (trimmedSerial === "") {
          setError("Serial is required.");
          return;
        }
        if (
          submittedTypeRaw !== "device" &&
          submittedTypeRaw !== "clamp" &&
          submittedTypeRaw !== "pcba"
        ) {
          setError("Type is required.");
          return;
        }
        const submittedType = submittedTypeRaw;
        if (submittedType === "pcba" && submittedPcbaProductFamily === "") {
          setError("PCBA product line is required.");
          return;
        }
        if (failureModes.length === 0) {
          setError("Failure mode is required.");
          return;
        }
        setError(null);
        createMutation.mutate({
          type: submittedType,
          subtype: submittedType === "clamp" ? submittedSubtype || null : null,
          serial: trimmedSerial,
          pcba_product_family:
            submittedType === "pcba" && submittedPcbaProductFamily !== ""
              ? submittedPcbaProductFamily
              : null,
          pcba_tla_variant:
            submittedType === "pcba" && submittedPcbaTlaVariant !== ""
              ? submittedPcbaTlaVariant
              : null,
          issue: failureModes.map((mode) => `${mode.category} > ${mode.name}`).join("; "),
          issue_notes: submittedIssueNotes || null,
          issue_date: null,
          failureModeIds: failureModes.map((mode) => mode.id),
          device_label_serial: identityPrefill?.deviceLabelSerial?.trim() || null,
          clamp_serial: identityPrefill?.clampSerial?.trim() || null,
          mcu_uuid: identityPrefill?.mcuUuid?.trim() || null,
        });
      }}
    >
      {error ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </div>
      ) : null}

      {identityPrefill != null &&
      ((identityPrefill.deviceLabelSerial ?? "").trim() !== "" ||
        (identityPrefill.clampSerial ?? "").trim() !== "" ||
        (identityPrefill.mcuUuid ?? "").trim() !== "") ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          <p className="font-medium text-slate-900">Also saving from Configuration</p>
          <ul className="mt-1 space-y-0.5 font-mono">
            {(identityPrefill.deviceLabelSerial ?? "").trim() !== "" ? (
              <li>Device label {identityPrefill.deviceLabelSerial?.trim()}</li>
            ) : null}
            {(identityPrefill.clampSerial ?? "").trim() !== "" ? (
              <li>Clamp {identityPrefill.clampSerial?.trim()}</li>
            ) : null}
            {(identityPrefill.mcuUuid ?? "").trim() !== "" ? (
              <li>MCU UUID {identityPrefill.mcuUuid?.trim()}</li>
            ) : null}
          </ul>
        </div>
      ) : null}

      <div>
        <Label htmlFor={serialFieldId}>Serial</Label>
        <Input
          ref={serialInputRef}
          id={serialFieldId}
          name="serial"
          value={serial}
          onChange={(event) => setSerial(event.target.value)}
          className="mt-1 font-mono"
          autoComplete="off"
          autoFocus
          {...FLOOR_PAGE_SCAN_TARGET_PROPS}
        />
        {serialHintsLoading ? (
          <p className="mt-1 text-xs text-slate-500">Looking up serial…</p>
        ) : openIncidentId != null ? (
          <p className="mt-1 text-xs text-amber-800">
            Open incident #{openIncidentId} already exists for this serial. Resolve it or use a different serial.
          </p>
        ) : null}
      </div>

      {lockedType == null ? (
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label htmlFor={typeFieldId}>Type</Label>
            <select
              id={typeFieldId}
              name="type"
              value={type}
              onChange={(event) => {
                fieldsTouchedRef.current.type = true;
                const nextType = event.target.value;
                if (nextType === "") {
                  setType("");
                  setSubtype(null);
                  setPcbaProductFamily("");
                  setPcbaTlaVariant("");
                  return;
                }
                const parsedType = nextType as QcIncidentType;
                setType(parsedType);
                setSubtype(defaultSubtypeForType(parsedType));
                if (parsedType !== "pcba") {
                  setPcbaProductFamily("");
                  setPcbaTlaVariant("");
                }
              }}
              className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">Select type…</option>
              <option value="device">Device</option>
              <option value="clamp">Clamp</option>
              <option value="pcba">PCBA</option>
            </select>
          </div>

          <div>
            <Label htmlFor={subtypeFieldId}>Subtype</Label>
            <select
              id={subtypeFieldId}
              name="subtype"
              value={subtype ?? ""}
              disabled={type !== "clamp"}
              onChange={(event) => {
                fieldsTouchedRef.current.subtype = true;
                setSubtype(event.target.value as QcIncidentSubtype);
              }}
              className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100 disabled:text-slate-500"
            >
              {type === "clamp" ? (
                <>
                  <option value="large">Large</option>
                  <option value="small">Small</option>
                </>
              ) : (
                <option value="">Not applicable</option>
              )}
            </select>
          </div>
        </div>
      ) : (
        <input type="hidden" name="type" value={lockedType} />
      )}

      {effectiveType === "pcba" ? (
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label htmlFor={pcbaProductFamilyFieldId}>PCBA product line</Label>
            <select
              id={pcbaProductFamilyFieldId}
              name="pcba_product_family"
              value={pcbaProductFamily}
              onChange={(event) => {
                fieldsTouchedRef.current.pcbaProductFamily = true;
                setPcbaProductFamily(event.target.value as QcIncidentPcbaProductFamily | "");
              }}
              className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">Select product line…</option>
              <optgroup label="FI-3C">
                {PCBA_PRODUCT_FAMILY_OPTIONS.filter((option) => option.group === "FI-3C").map(
                  (option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ),
                )}
              </optgroup>
              <optgroup label="FI-5A">
                {PCBA_PRODUCT_FAMILY_OPTIONS.filter((option) => option.group === "FI-5A").map(
                  (option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ),
                )}
              </optgroup>
              <optgroup label="DM-1">
                {PCBA_PRODUCT_FAMILY_OPTIONS.filter((option) => option.group === "DM-1").map(
                  (option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ),
                )}
              </optgroup>
            </select>
          </div>
          <div>
            <Label htmlFor={pcbaTlaVariantFieldId}>Clamp TLA variant</Label>
            <select
              id={pcbaTlaVariantFieldId}
              name="pcba_tla_variant"
              value={pcbaTlaVariant}
              onChange={(event) => {
                fieldsTouchedRef.current.pcbaTlaVariant = true;
                setPcbaTlaVariant(event.target.value as QcIncidentPcbaTlaVariant | "");
              }}
              className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">Not applicable / unknown</option>
              <option value="A">A (for example FI-3C-B-TLA-A)</option>
              <option value="C">C (for example FI-3C-B-TLA-C)</option>
            </select>
          </div>
          <p className="md:col-span-2 text-xs text-slate-500">
            Product line is required. Select A or C when a clamp is attached so Kodiak TLA planning
            can match the correct TLA row.
          </p>
        </div>
      ) : null}

      <FailureModeTreeSelect
        id={failureModeFieldId}
        options={failureModesQuery.data?.rows ?? []}
        value={failureModes}
        onChange={setFailureModes}
        loading={failureModesQuery.isLoading}
      />

      <div>
        <Label htmlFor={notesFieldId}>Notes</Label>
        <TextArea
          id={notesFieldId}
          name="issue_notes"
          value={issueNotes}
          onChange={(event) => setIssueNotes(event.target.value)}
          className="mt-1 font-sans"
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <Label>Issue date</Label>
          <div className="mt-1 flex min-h-9 w-full items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-800">
            {todayLabel()}
          </div>
        </div>
        <div>
          <Label>Opened by</Label>
          <div className="mt-1 flex min-h-9 items-center">
            <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white py-0.5 pl-0.5 pr-2.5 text-xs font-medium text-slate-800">
              <HubUserAvatar
                avatarUrl={meQuery.data?.user.avatarUrl}
                label={openerDisplay}
                size="sm"
                className="h-5 w-5 text-[10px]"
              />
              <span className="truncate">{openerDisplay}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
        {onCancel ? (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={createMutation.isPending}>
            Cancel
          </Button>
        ) : null}
        <HoverHintTrigger
          trigger={
            <Button type="submit" isLoading={createMutation.isPending}>
              Open quality event
            </Button>
          }
        >
          Control+enter to submit; enter to go to next field
        </HoverHintTrigger>
      </div>
    </form>
  );
}
